import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Building2, MapPin, Navigation, Pencil, Trash2, Plus, X, Users } from 'lucide-react';
import HorarioSemanalInput from './HorarioSemanalInput';
import { toast } from 'sonner';
import CentroMapPicker, { geocodificarDireccion } from './CentroMapPicker';

const CCAA = [
  ['AN', 'Andalucía'], ['AR', 'Aragón'], ['AS', 'Asturias'], ['CN', 'Canarias'],
  ['CB', 'Cantabria'], ['CL', 'Castilla y León'], ['CM', 'Castilla-La Mancha'],
  ['CT', 'Cataluña'], ['EX', 'Extremadura'], ['GA', 'Galicia'], ['IB', 'Islas Baleares'],
  ['RI', 'La Rioja'], ['MD', 'Madrid'], ['MC', 'Murcia'], ['NC', 'Navarra'],
  ['PV', 'País Vasco'], ['VC', 'Comunidad Valenciana'], ['CE', 'Ceuta'], ['ML', 'Melilla'],
];
const CCAA_NAME = Object.fromEntries(CCAA);
const emptyCentro = { nombre: '', direccion: '', municipio: '', provincia: '', comunidad: '', lat: '', lng: '', radio_m: 100, horario_semanal: {} };

const CentrosTab = () => {
  const [centros, setCentros] = useState([]);
  const [form, setForm] = useState(emptyCentro);
  const [editando, setEditando] = useState(null);
  const [geocodificando, setGeocodificando] = useState(false);

  const cargar = () => api.get('/admin/centros').then((r) => setCentros(r.data)).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const resetForm = () => { setEditando(null); setForm(emptyCentro); };

  const abrirEdicion = (c) => {
    setEditando(c);
    setForm({
      nombre: c.nombre, direccion: c.direccion || '', municipio: c.municipio || '',
      provincia: c.provincia || '', comunidad: c.comunidad || '',
      lat: c.lat ?? '', lng: c.lng ?? '', radio_m: c.radio_m ?? 100,
      horario_semanal: c.horario_semanal || {},
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre del centro es obligatorio'); return; }
    const latNum = form.lat === '' || form.lat === null ? null : parseFloat(form.lat);
    const lngNum = form.lng === '' || form.lng === null ? null : parseFloat(form.lng);
    if ((latNum === null) !== (lngNum === null)) { toast.error('Indica latitud y longitud juntas, o deja ambas vacías'); return; }
    if (latNum !== null && (isNaN(latNum) || isNaN(lngNum) || latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180)) {
      toast.error('Coordenadas GPS no válidas'); return;
    }
    const payload = {
      nombre: form.nombre, direccion: form.direccion, municipio: form.municipio,
      provincia: form.provincia, comunidad: form.comunidad, lat: latNum, lng: lngNum,
      radio_m: parseInt(form.radio_m, 10) || 100,
      horario_semanal: form.horario_semanal || {},
    };
    try {
      if (editando) {
        await api.put(`/admin/centros/${editando.id}`, payload);
        toast.success('Centro actualizado');
      } else {
        await api.post('/admin/centros', payload);
        toast.success('Centro creado');
      }
      resetForm(); cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const eliminar = async (id) => {
    try {
      await api.delete(`/admin/centros/${id}`);
      toast.success('Centro eliminado');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const ubicarPorDireccion = async () => {
    const partes = [form.direccion, form.municipio, form.provincia].map((s) => (s || '').trim()).filter(Boolean);
    if (partes.length === 0) { toast.error('Rellena la dirección, el municipio o la provincia primero'); return; }
    setGeocodificando(true);
    try {
      const r = await geocodificarDireccion(partes.join(', ') + ', España');
      if (r) { setForm((f) => ({ ...f, lat: r.lat, lng: r.lng })); toast.success('Ubicación fijada desde la dirección'); }
      else toast.error('No se encontró la dirección. Ajusta el pin en el mapa manualmente.');
    } catch { toast.error('No se pudo geolocalizar la dirección'); }
    finally { setGeocodificando(false); }
  };

  const usarMiUbicacion = () => {
    if (!navigator.geolocation) { toast.error('Tu navegador no soporta geolocalización'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })); toast.success('Ubicación actual cargada'); },
      () => toast.error('No se pudo obtener la ubicación'),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="centros-tab">
      {/* Formulario ------------------------------------------------- */}
      <Card className="border-zinc-200 lg:col-span-3 h-fit">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Building2 className="h-5 w-5" /> {editando ? `Editar: ${editando.nombre}` : 'Nuevo centro de trabajo'}
          </CardTitle>
          <CardDescription>Cada centro tiene su propio calendario laboral y su ubicación GPS para validar los fichajes desde la app.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nombre *</Label>
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Sede Madrid" data-testid="centro-nombre" />
          </div>
          <div className="space-y-1.5">
            <Label>Comunidad Autónoma</Label>
            <Select value={form.comunidad} onValueChange={(v) => setForm({ ...form, comunidad: v })}>
              <SelectTrigger data-testid="centro-comunidad"><SelectValue placeholder="Selecciona (necesario para importar festivos)" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {CCAA.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Municipio</Label>
              <Input value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })} data-testid="centro-municipio" />
            </div>
            <div className="space-y-1.5">
              <Label>Provincia</Label>
              <Input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} data-testid="centro-provincia" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Dirección</Label>
            <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Calle, número, código postal…" data-testid="centro-direccion" />
          </div>
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="flex items-center gap-1.5 text-zinc-700"><MapPin className="h-4 w-4" /> Ubicación GPS (validación en la app del empleado)</Label>
              <div className="flex items-center gap-1.5">
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={ubicarPorDireccion} disabled={geocodificando} data-testid="centro-usar-direccion">
                  <MapPin className="h-3.5 w-3.5 mr-1" /> {geocodificando ? 'Buscando…' : 'Usar la dirección'}
                </Button>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={usarMiUbicacion} data-testid="centro-usar-ubicacion">
                  <Navigation className="h-3.5 w-3.5 mr-1" /> Mi ubicación
                </Button>
              </div>
            </div>
            <p className="text-xs text-zinc-500">Si defines coordenadas, los fichajes desde la app se validan por cercanía. Fuera del radio se permiten, pero se marcan como incidencia. Déjalo vacío para no exigir ubicación.</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Latitud</Label>
                <Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="40.4168" data-testid="centro-lat" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitud</Label>
                <Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder="-3.7038" data-testid="centro-lng" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Radio (m)</Label>
                <Input type="number" min="10" step="10" value={form.radio_m} onChange={(e) => setForm({ ...form, radio_m: e.target.value })} data-testid="centro-radio" />
              </div>
            </div>
            <CentroMapPicker lat={form.lat} lng={form.lng} radio={form.radio_m}
              onChange={(la, ln) => setForm((f) => ({ ...f, lat: la, lng: ln }))} />
          </div>
          <div className="pt-2 border-t border-zinc-100 space-y-2">
            <div>
              <Label className="text-sm">Horario semanal del centro (opcional)</Label>
              <p className="text-xs text-zinc-500">Horas por día que los empleados heredarán si su modo de horas es &quot;Heredar centro&quot;. Deja en blanco un día para marcarlo como no laborable.</p>
            </div>
            <HorarioSemanalInput value={form.horario_semanal} onChange={(h) => setForm({ ...form, horario_semanal: h })} testidPrefix="centro-hs" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button className="bg-zinc-900 hover:bg-zinc-800 flex-1" onClick={guardar} data-testid="save-centro-btn">
              {editando ? 'Guardar cambios' : (<><Plus className="h-4 w-4 mr-1.5" /> Crear centro</>)}
            </Button>
            {editando && <Button variant="outline" onClick={resetForm} data-testid="cancel-edit-centro">Cancelar</Button>}
          </div>
        </CardContent>
      </Card>

      {/* Listado ---------------------------------------------------- */}
      <div className="lg:col-span-2 space-y-3">
        <Card className="border-zinc-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">Centros ({centros.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {centros.length === 0 && (
              <p className="text-sm text-zinc-400 text-center py-6">Aún no hay centros creados.<br />Rellena el formulario para crear el primero.</p>
            )}
            {centros.map((c) => (
              <div key={c.id} className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2.5" data-testid={`centro-item-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-zinc-800 truncate">{c.nombre}</p>
                    {(c.municipio || c.comunidad) && (
                      <p className="text-xs text-zinc-500 truncate flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" />
                        {[c.municipio, CCAA_NAME[c.comunidad]].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => abrirEdicion(c)} data-testid={`edit-centro-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" data-testid={`delete-centro-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar centro «{c.nombre}»?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción borrará el centro y sus festivos asociados. Los empleados que lo tuvieran asignado se quedarán sin centro (podrás reasignarlos después).
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => eliminar(c.id)}>Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {c.comunidad && <Badge variant="outline" className="text-xs">{CCAA_NAME[c.comunidad] || c.comunidad}</Badge>}
                  <Badge variant="secondary" className="text-xs"><Users className="h-3 w-3 mr-1" />{c.num_empleados} empleados</Badge>
                  <Badge variant="secondary" className="text-xs">{c.num_festivos} festivos</Badge>
                  {c.lat != null && c.lng != null ? (
                    <Badge className="text-xs bg-emerald-600 hover:bg-emerald-600"><MapPin className="h-3 w-3 mr-0.5" />GPS · {c.radio_m}m</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-zinc-500">Sin GPS</Badge>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        <p className="text-xs text-zinc-400 leading-relaxed">
          El calendario laboral (festivos por comunidad y locales) se gestiona en la pestaña <strong>Calendario</strong>, filtrado por centro.
        </p>
      </div>
    </div>
  );
};

export default CentrosTab;
